import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Space, TwoFactorAuthModal } from '@cashfree-intl/coherent';
import { SubStepDescription } from 'containers/CheckoutJourney/screens/styled';

import { useInjectReducer } from 'utils/injectReducer';
import { useInjectSaga } from 'utils/injectSaga';
import { exportToCSV } from 'utils/common';

import { showToast } from 'containers/Toast/actions';
import { sendAnalyticsData } from 'containers/ProductTour/helper';
import { INTEGRATION_STATUS } from 'containers/CheckoutJourney/util/constant';
import { TWO_FA_TYPE } from 'utils/constants';
import { getInfoText } from 'utils/modal';

import ApiKeyReducer from 'containers/ApiKeys/reducer';
import ApiKeySaga from 'containers/ApiKeys/saga';
import makeSelectApiKeys from 'containers/ApiKeys/selectors';

import {
  fetchAPIKeysAction,
  generateAPIKeyAction,
} from 'containers/ApiKeys/actions';

import ApiKeyModal from 'components/ApiKeyModal';

import {
  ApiKeysState,
  SubstepComponentProps,
} from 'containers/CheckoutJourney/types';
import TwoFAProvider from 'providers/TwoFAProvider';
import { useTwoFAData } from 'hooks/useTwoFAData';

// Initial Auth Info
const initialAuthInfo = {
  code: '',
};

// Define Component
const ProductionApiKeysComponent = ({
  substep,
  updateStatus,
}: SubstepComponentProps) => {
  const dispatch = useDispatch();
  const apiKeys: ApiKeysState = useSelector(makeSelectApiKeys());
  const {
    open2FA,
    close2FA,
    confirm2FA,
    sendOtp,
    is2FAVerified,
    modeActive,
    activeMethodData,
  } = useTwoFAData();

  useInjectReducer({ key: 'apiKeys', reducer: ApiKeyReducer });
  useInjectSaga({ key: 'apiKeys', saga: ApiKeySaga, mode: '' });

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [otpVerificationStarted, setOtpVerificationStarted] = useState<boolean>(
    false,
  );

  const { isLoading, data, apiKeyModalResponse } = apiKeys;

  useEffect(() => {
    dispatch(fetchAPIKeysAction({ options: {} }));
  }, [dispatch]);

  // Function to generate/fetch API keys
  const generateApiKeys = () => {
    setIsGenerating(true);
    dispatch(
      generateAPIKeyAction({
        successCb: () => {
          setIsGenerating(false);
          setModalOpen(true);
        },
        failureCb: (err: { message: string }) => {
          setIsGenerating(false);
          if (err.message === 'Active Merchant key already exists') {
            dispatch(
              fetchAPIKeysAction({
                options: {},
                successCb: () => setModalOpen(true),
              }),
            );
          } else {
            showToast({
              message: err.message || 'Error generating API key',
              type: 'error',
            });
          }
        },
      }),
    );
    sendAnalyticsData('procureProductionAPIKeysButtonClicked');
  };

 
  const handleAfterOtpVerification = () => {
    setOtpVerificationStarted(false);
    generateApiKeys();
  };

  // Connect to the 2FA confirmation
  const onConfirmOtp = confirm2FA({
    cb: handleAfterOtpVerification,
  });

  // Handle the button click
  const handleGenerateClick = () => {
    // Start OTP verification
    setOtpVerificationStarted(true);
    open2FA({
      cb: handleAfterOtpVerification,
    });
  };

  const closeModal = () => {
    setModalOpen(false);
    updateStatus(substep.id, INTEGRATION_STATUS.COMPLETED);
  };

  const downloadAPIKeyFn = () => {
    exportToCSV('APIKey.csv', data);
    setModalOpen(false);
    updateStatus(substep.id, INTEGRATION_STATUS.COMPLETED);
  };

  const isButtonLoading = isLoading || isGenerating;

  const getModalData = () => {
    if (apiKeyModalResponse && Object.keys(apiKeyModalResponse).length > 0) {
      return apiKeyModalResponse;
    }
    if (data && data.length > 0) {
      return data[0];
    }
    return null;
  };

  const modalData = getModalData();

  return (
    <Space gap={2.5} direction="column" className="mt-2">
      <SubStepDescription color="bodyLight">
        {substep.description}
      </SubStepDescription>

      <Space direction="row" gap={1}>
        <Button
          primary
          onClick={handleGenerateClick}
          loading={isButtonLoading}
          disabled={isButtonLoading}
        >
          Access Prod API Keys
        </Button>
      </Space>
      {modalOpen && (
        <ApiKeyModal
          open={modalOpen}
          data={modalData}
          isViewMode={false}
          closeModal={closeModal}
          downloadClick={downloadAPIKeyFn}
        />
      )}

      {otpVerificationStarted && !is2FAVerified && (
        <>
          {modeActive === TWO_FA_TYPE.G2FA && (
            <TwoFactorAuthModal
              open
              type={TWO_FA_TYPE.G2FA}
              infoLabel={
                <>
                  Enter the 6-digit authentication code from your authenticator
                  app
                </>
              }
              authInfo={initialAuthInfo}
              onConfirm={onConfirmOtp}
              onClose={() => {
                close2FA();
                setOtpVerificationStarted(false);
              }}
              onResend={async () => {}}
            />
          )}
          {modeActive === TWO_FA_TYPE.OTP && (
            <TwoFactorAuthModal
              open
              type={TWO_FA_TYPE.OTP}
              infoLabel={getInfoText(activeMethodData)}
              authInfo={initialAuthInfo}
              onConfirm={onConfirmOtp}
              onClose={() => {
                close2FA();
                setOtpVerificationStarted(false);
              }}
              onResend={sendOtp}
            />
          )}
        </>
      )}
    </Space>
  );
};

const ProductionApiKeys = (props: SubstepComponentProps) => (
  <TwoFAProvider>
    <ProductionApiKeysComponent {...props} />
  </TwoFAProvider>
);

export { ProductionApiKeys };
